import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import {
  NOTIFICATION_CATEGORIES,
  POSITION_STATUSES,
  isJobBuilderSection,
  sectionForWizardStep,
  type NotificationCategory,
  type PositionStatus,
  type RequisitionDetail,
} from '@nest-recruit/shared';
import type { ReactNode } from 'react';
import { NotFoundState } from './components/system-states/SystemState';
import { ToastStack } from './components/ToastStack';
import { AcceptInvitePage } from './features/auth/pages/AcceptInvitePage';
import { ForgotPasswordPage } from './features/auth/pages/ForgotPasswordPage';
import { LoginPage } from './features/auth/pages/LoginPage';
import { OtpChallengePage } from './features/auth/pages/OtpChallengePage';
import { ResetPasswordPage } from './features/auth/pages/ResetPasswordPage';
import { AccountLockedPage, SessionExpiredPage } from './features/auth/pages/StatusPages';
import { TwoFactorSetupPage } from './features/auth/pages/TwoFactorSetupPage';
import { WelcomePage } from './features/auth/pages/WelcomePage';
import { AccessAuditPage } from './features/admin/pages/AccessAuditPage';
import { readAccessLogSearch, type AccessLogSearch } from './features/admin/components/access-log/access-log-address';
import { AdminLanding, AdminLayout } from './features/admin/pages/AdminLayout';
import { AdminOverviewPage } from './features/admin/pages/AdminOverviewPage';
import { CalendarsPage } from './features/admin/pages/CalendarsPage';
import { EmailDeliveryPage } from './features/admin/pages/EmailDeliveryPage';
import { BackgroundWorkPage } from './features/admin/pages/BackgroundWorkPage';
import { readEmailDeliverySearch, type EmailDeliverySearch } from './features/admin/components/email-delivery/email-delivery-address';
import { readBackgroundWorkSearch, type BackgroundWorkSearch } from './features/admin/components/background-work/background-work-address';
import { EventLogPage } from './features/admin/pages/EventLogPage';
import { DelegationPage } from './features/admin/pages/DelegationPage';
import { InviteUserPage } from './features/admin/pages/InviteUserPage';
import { NewJobPage } from './features/jobs/pages/NewJobPage';
import { MasterSettingsPage } from './features/admin/pages/MasterSettingsPage';
import { RoleBuilderPage } from './features/admin/pages/RoleBuilderPage';
import { readRolesSearch, type RolesSearch } from './features/admin/components/roles/roles-address';
import { UserDetailPage } from './features/admin/pages/UserDetailPage';
import { UserListPage } from './features/admin/pages/UserListPage';
import { SESSION_QUERY_KEY, fetchSession } from './features/auth/api/auth-api';
import { APP_SETTINGS_KEY, fetchAppSettings } from './lib/app-settings';
import { PREFERENCES_KEY, loadPreferencesFor } from './lib/load-preferences';
import { ShellSkeleton } from './features/auth/components/ShellSkeleton';
import { RouteErrorState } from './components/system-states/RouteErrorState';
import { DemoPage } from './features/demo/DemoPage';
import { HeadcountPlanPage } from './features/organisation/pages/HeadcountPlanPage';
import { OrgTreePage } from './features/organisation/pages/OrgTreePage';
import { PositionDetailPage } from './features/organisation/pages/PositionDetailPage';
import { PositionLedgerPage } from './features/organisation/pages/PositionLedgerPage';
import type { PositionLedgerSearch } from './features/organisation/pages/PositionLedgerPage';
import { NotificationInboxPage } from './features/notifications/pages/NotificationInboxPage';
import type { NotificationInboxSearch } from './features/notifications/pages/NotificationInboxPage';
import { NotificationPreferencesPage } from './features/notifications/pages/NotificationPreferencesPage';
import { PlaceholderPage } from './features/placeholder/PlaceholderPage';
import { PoolPage, type PoolPageSearch } from './features/pools/pages/PoolPage';
import { PoolsAndTagsPage } from './features/pools/pages/PoolsAndTagsPage';
import { InboxPage } from './features/communication/pages/InboxPage';
import { MessageTemplatesPage } from './features/communication/pages/MessageTemplatesPage';
import { MyTasksPage } from './features/communication/pages/MyTasksPage';
import { HomePage } from './features/workspace/pages/HomePage';
import { readCommandCenterUrl, readDrillUrl, type CommandCenterUrl } from './features/reports/api/reports-api';
import { readAnalyticsDrillUrl, readAnalyticsUrl, readBuilderUrl, readDropOffUrl, type AnalyticsUrl, type BuilderUrl, type DropOffUrl } from './features/reports/api/analytics-api';
import { DrillPage, type DrillPageUrl } from './features/reports/pages/DrillPage';
import { DropOffPage } from './features/reports/pages/DropOffPage';
import { ProficiencyPage } from './features/reports/pages/ProficiencyPage';
import { ReportBuilderPage } from './features/reports/pages/ReportBuilderPage';
import { ScoreboardPage } from './features/reports/pages/ScoreboardPage';
import { CommandCenterPage } from './features/reports/pages/CommandCenterPage';
import { MetricDictionaryPage } from './features/reports/pages/MetricDictionaryPage';
import { RetentionPage, type RetentionUrl } from './features/retention/pages/RetentionPage';
import { ReportsIndexPage } from './features/reports/pages/ReportsIndexPage';
import { WorkspacePage } from './features/workspace/pages/WorkspacePage';
import { PoolsPage } from './features/pools/pages/PoolsPage';
import { CvBankPage, type CvBankSearch } from './features/cv-bank/pages/CvBankPage';
import { readSearchResults, SearchResultsPage, type SearchResultsUrl } from './features/pools/pages/SearchResultsPage';
import { ApprovalChainsPage, readApprovalChainsSearch, type ApprovalChainsSearch } from './features/requisitions/pages/ApprovalChainsPage';
import { TimeLimitsPage } from './features/requisitions/pages/TimeLimitsPage';
import { ApprovalQueuePage } from './features/requisitions/pages/ApprovalQueuePage';
import {
  RequisitionBoardPage,
  type RequisitionBoardSearch,
} from './features/requisitions/pages/RequisitionBoardPage';
import {
  RequisitionDetailPage,
  type RequisitionDetailSearch,
} from './features/requisitions/pages/RequisitionDetailPage';
import { MyActionsPage, type MyActionsSearch } from './features/requisitions/pages/MyActionsPage';
import { ManagerShortlistPage, type ManagerShortlistSearch } from './features/shortlist/pages/ManagerShortlistPage';
import { MyOpeningsPage, type MyOpeningsSearch } from './features/requisitions/pages/MyOpeningsPage';
import { RequestHirePage } from './features/requisitions/pages/RequestHirePage';
import { hmLandingFor, isWaitingOnOwner } from './features/requisitions/hm-landing';
import { JobListPage } from './features/jobs/pages/JobListPage';
import { readJobListSearch, type JobListSearch } from './features/jobs/pages/job-list-address';
import { QuestionBankPage, type QuestionBankSearch } from './features/assessments/pages/QuestionBankPage';
import { TestBuilderPage } from './features/assessments/pages/TestBuilderPage';
import { TestPlayerPage } from './features/assessments/pages/TestPlayerPage';
import { TestsPage } from './features/assessments/pages/TestsPage';
import { ExamSessionPage } from './features/assessments/pages/ExamSessionPage';
import { ExamSessionsPage } from './features/assessments/pages/ExamSessionsPage';
import { MarkingPage } from './features/assessments/pages/MarkingPage';
import { MarkingQueuePage } from './features/assessments/pages/MarkingQueuePage';
import { PrintRosterPage } from './features/assessments/pages/PrintRosterPage';
import { InterviewBookingPage } from './features/interviews/pages/InterviewBookingPage';
import { ScorecardLinkPage } from './features/scorecards/pages/ScorecardLinkPage';
import { CandidateOfferPage } from './features/offers/pages/CandidateOfferPage';
import { OfferLettersPage } from './features/offers/pages/OfferLettersPage';
import { OfferPage } from './features/offers/pages/OfferPage';
import { ClosurePage } from './features/hires/pages/ClosurePage';
import { HirePage } from './features/hires/pages/HirePage';
import { HirePlansPage } from './features/hire-plans/pages/HirePlansPage';
import { InterviewCalendarPage } from './features/interviews/pages/InterviewCalendarPage';
import { InterviewPage } from './features/interviews/pages/InterviewPage';
import { MyAvailabilityPage } from './features/interviews/pages/MyAvailabilityPage';
import { MyInterviewsPage } from './features/interviews/pages/MyInterviewsPage';
import { SchedulerPage } from './features/interviews/pages/SchedulerPage';
import { CockpitPage } from './features/scorecards/pages/CockpitPage';
import { InterviewScorecardsPage } from './features/scorecards/pages/InterviewScorecardsPage';
import { DebriefPage } from './features/decisions/pages/DebriefPage';
import { ToSchedulePage } from './features/interviews/pages/ToSchedulePage';
import { AddCandidatePage } from './features/candidates/pages/AddCandidatePage';
import { ApplicationPage } from './features/candidates/pages/ApplicationPage';
import { CandidateProfilePage, readCandidateProfileSearch, type CandidateProfileSearch } from './features/candidates/pages/CandidateProfilePage';
import { CandidatesPage } from './features/candidates/pages/CandidatesPage';
import { legacySearchToBrowse, readBrowseSearch, type CandidateBrowseSearch } from './features/candidates/components/list/browse-address';
import { DuplicatesPage } from './features/candidates/pages/DuplicatesPage';
import { MergePage, type MergeSearch } from './features/candidates/pages/MergePage';
import { CareerSitePage } from './features/jobs/pages/CareerSitePage';
import { JobHubPage, jobPageViewOf } from './features/jobs/pages/JobHubPage';
import { ReferralsPage } from './features/jobs/pages/ReferralsPage';
import { JobStartPage } from './features/jobs/pages/JobStartPage';
import { InterviewKitEditorPage } from './features/jobs/pages/InterviewKitEditorPage';
import { InterviewKitsPage } from './features/jobs/pages/InterviewKitsPage';
import { PipelineEditorPage } from './features/jobs/pages/PipelineEditorPage';
import { PipelinesPage } from './features/jobs/pages/PipelinesPage';
import { PlanTemplateEditorPage } from './features/jobs/pages/PlanTemplateEditorPage';
import { PlanTemplatesPage } from './features/jobs/pages/PlanTemplatesPage';
import { RoleTemplateEditorPage } from './features/jobs/pages/RoleTemplateEditorPage';
import { RoleTemplatesPage } from './features/jobs/pages/RoleTemplatesPage';
import { RubricEditorPage } from './features/jobs/pages/RubricEditorPage';
import { RubricsPage } from './features/jobs/pages/RubricsPage';
import { ReasonsPage } from './features/admin/pages/ReasonsPage';
import { CustomFieldsPage } from './features/admin/pages/CustomFieldsPage';
import { FxRatesPage } from './features/admin/pages/FxRatesPage';
import { NameMatchingPage } from './features/admin/pages/NameMatchingPage';
import { SourcesPage } from './features/admin/pages/SourcesPage';
import { ToolNamesPage } from './features/admin/pages/ToolNamesPage';
import { StaffListPage } from './features/admin/pages/StaffListPage';
import { SystemSettingsPage } from './features/admin/pages/SystemSettingsPage';
import { RoomsPage } from './features/admin/pages/RoomsPage';
import { AutoRejectedPage } from './features/sorting/pages/AutoRejectedPage';
import { SortingPage } from './features/sorting/pages/SortingPage';
import { validatePipelineSearch, type PipelineSearch } from './features/pipeline/pipeline-search';
import { REQUISITIONS_KEY } from './features/requisitions/api/requisitions-api';
import { apiGet } from './lib/api-client';
import { AppShell } from './shell/AppShell';
import { NAV_ITEMS } from './shell/nav-config';
import { safeReturnPath } from './features/auth/return-url';
import { SESSION_ENDED_REASON } from './features/auth/session-ended';
import type { DemoSearch } from './features/demo/DemoTable';
import type { DemoStage } from './features/demo/mock-rows';

// Code-based route tree, so there is no generated file to keep in step.
//
// CLAUDE.md §5a rule 5: NO route-level code splitting. Every screen ships in the
// main bundle, so a route transition is a component swap rather than a fetch.
// Only genuinely heavy, rarely-used chunks are lazy-loaded, and none exist yet.
//
// Two layouts hang off the root:
//
// - **`authLayout`** — AUTH-01 to AUTH-09. No shell, because a signed-out visitor
//   has no module bar, no notifications and no scope to switch.
// - **`appLayout`** — everything else, gated in `beforeLoad`. The shell mounts once
//   inside it and never re-renders on navigation (CLAUDE.md §5a rule 6).
//
// **The gate is `beforeLoad`, not a component.** A component that renders
// `<Navigate>` while it is still mounted renders it again on the next commit, and
// with a fresh `search` object each time that is an infinite redirect — which is
// exactly what happened when this was tried the other way round. `beforeLoad` runs
// before the route renders at all, so there is no flash of a shell the visitor is
// not allowed to see, and no loop to guard against.

interface RouterContext {
  readonly queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootFrame,
  notFoundComponent: NotFound,
});

/**
 * The session, from cache when it is there and from the API when it is not.
 *
 * `ensureQueryData` is what makes this cheap: the shell's own `useSessionQuery`
 * reads the same cache entry, so the gate and the screen never disagree and the
 * request happens once.
 */
async function loadSession(queryClient: QueryClient) {
  return queryClient.ensureQueryData({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchSession,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

/**
 * Above both layouts sits only the toast stack — feedback has to survive a route
 * change, and an undo toast raised on the way out of a screen must still be there
 * to be clicked.
 */
function RootFrame(): ReactNode {
  return (
    <>
      <Outlet />
      <ToastStack />
    </>
  );
}

/**
 * "Go back" means the page the user came from. Home is only the fallback, for a
 * 404 opened in a fresh tab or from a link elsewhere, where there is nothing to
 * go back to inside the app.
 */
function NotFound(): ReactNode {
  const navigate = useNavigate();
  const router = useRouter();
  const goBack = (): void => {
    if (router.history.canGoBack()) {
      router.history.back();
      return;
    }
    void navigate({ to: '/' });
  };
  return <NotFoundState onGoBack={goBack} />;
}

/** `?next=` on the auth screens. A path only — `safeReturnPath` rejects the rest. */
function validateNext(search: Record<string, unknown>): { next?: string } {
  return typeof search.next === 'string' && search.next !== '' ? { next: search.next } : {};
}

/** `?next=` plus `?reason=` on sign-in, so it can say why the person is there. */
function validateLogin(search: Record<string, unknown>): { next?: string; reason?: string } {
  return {
    ...validateNext(search),
    ...(search.reason === SESSION_ENDED_REASON ? { reason: SESSION_ENDED_REASON } : {}),
  };
}

/**
 * `?next=` plus `?remember=0` on the code challenge. The tick box lives on the
 * sign-in form, but the real cookie is not issued until the code is accepted —
 * so the choice has to travel one screen, and it travels in the URL so a reload
 * mid-challenge falls back to the default rather than to a guess.
 */
function validateVerify(search: Record<string, unknown>): { next?: string; remember?: string } {
  return {
    ...validateNext(search),
    ...(search.remember === '0' ? { remember: '0' } : {}),
  };
}

/** `?token=` on the two link-driven screens. */
function validateToken(search: Record<string, unknown>): { token?: string } {
  return typeof search.token === 'string' && search.token !== '' ? { token: search.token } : {};
}

// --- The auth layout ---------------------------------------------------------

const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'auth-layout',
  component: () => <Outlet />,
});

/** Signed in already? These screens have nothing to offer. */
const signedOutOnlyRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  id: 'signed-out-only',
  beforeLoad: async ({ context }) => {
    const session = await loadSession(context.queryClient);
    if (session) throw redirect({ to: '/' });
  },
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => signedOutOnlyRoute,
  path: '/login',
  component: LoginPage,
  validateSearch: validateLogin,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => signedOutOnlyRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => signedOutOnlyRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
  validateSearch: validateToken,
});

/**
 * Outside the signed-out gate: a signed-in visitor opening an invite link is told who they are
 * signed in as and offered Sign out, rather than silently bounced home (Redesign Group 9).
 */
const acceptInviteRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/accept-invite',
  beforeLoad: async ({ context }) => {
    await loadSession(context.queryClient);
  },
  component: AcceptInvitePage,
  validateSearch: validateToken,
});

const sessionExpiredRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/session-expired',
  component: SessionExpiredPage,
  validateSearch: validateNext,
});

const accountLockedRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/account-locked',
  component: AccountLockedPage,
});

/**
 * The OTP challenge and the two-factor setup screen sit OUTSIDE the signed-out
 * gate: both are reached with a session in hand — a half-authenticated one in the
 * first case, a session that must enrol in the second.
 */
const otpChallengeRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/verify-code',
  component: OtpChallengePage,
  validateSearch: validateVerify,
});

const twoFactorSetupRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/two-factor-setup',
  component: TwoFactorSetupPage,
});

const welcomeRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/welcome',
  // A reload lands here with an empty cache. Without this the screen greets nobody
  // with the wrong role's briefing, and the button has no cached session to mark
  // seen — so the app gate bounces its first click straight back here.
  beforeLoad: async ({ context }) => {
    const session = await loadSession(context.queryClient);
    // D82: an Admin viewing as someone stays in the app, where the banner and Stop are.
    if (session?.isImpersonating) throw redirect({ to: '/' });
  },
  component: WelcomePage,
});

// --- ASM-03: the candidate's test (S17) --------------------------------------

/**
 * N-20's link. Outside both layouts: a candidate has no account, so there is no session
 * to check and no shell to show (D16). The link's token is read from the address once
 * and sent to the API in a header.
 */
const testPlayerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/test',
  validateSearch: validateToken,
  component: function TestPlayer() {
    const { token } = testPlayerRoute.useSearch();
    return <TestPlayerPage token={token ?? ''} />;
  },
});

// --- S19: the candidate's interview booking page ------------------------------------

/**
 * N-27 and N-29's link. Outside both layouts, like the test page: a candidate has no
 * account (D16). The token is read from the address once and sent in a header.
 */
const interviewBookingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/interview',
  validateSearch: validateToken,
  component: function InterviewBooking() {
    const { token } = interviewBookingRoute.useSearch();
    return <InterviewBookingPage token={token ?? ''} />;
  },
});

// --- C6 (ADR 0077): a panelist's scorecard by link -----------------------------------------

/**
 * N-101 and N-102's link (INT-04). Outside both layouts, like the booking page: a panelist may have no
 * account at all. The token is read from the address once and sent in a header.
 */
const scorecardLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/scorecard',
  validateSearch: validateToken,
  component: function ScorecardLink() {
    const { token } = scorecardLinkRoute.useSearch();
    return <ScorecardLinkPage token={token ?? ''} />;
  },
});

// --- S23: the candidate's offer page ---------------------------------------------------

/**
 * N-50 and N-51's link. Outside both layouts, like the test and booking pages: a candidate has
 * no account (D16). The token is read from the address once and sent in a header.
 */
const candidateOfferRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/offer',
  validateSearch: validateToken,
  component: function CandidateOffer() {
    const { token } = candidateOfferRoute.useSearch();
    return <CandidateOfferPage token={token ?? ''} />;
  },
});

// --- The application layout --------------------------------------------------

const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app-layout',
  beforeLoad: async ({ context, location }) => {
    const session = await loadSession(context.queryClient);

    if (!session) {
      // The acceptance criterion: remember where they were going, and put them
      // back there after signing in rather than on a dashboard they did not ask
      // for. `safeReturnPath` is what stops this becoming an open redirect.
      throw redirect({
        to: '/login',
        search: { next: safeReturnPath(`${location.pathname}${location.searchStr}`) },
      });
    }

    // 2FA is mandatory for HR Lead and Approver, and the server refuses every
    // other route until it is done — so anywhere else would be a wall of 403s.
    if (session.mustEnrolTwoFactor) throw redirect({ to: '/two-factor-setup' });

    // AUTH-09, once. `hasSeenWelcome` is true from then on. Never while viewing as someone (D82):
    // the Welcome page is for the person themself and has no banner or Stop.
    if (!session.hasSeenWelcome && !session.isImpersonating) throw redirect({ to: '/welcome' });

    // S31: dates, numbers, money and source names follow ADMIN-15 from the first paint. Once per
    // sign-in (cached); a failure keeps the defaults rather than blocking the app.
    await context.queryClient.prefetchQuery({ queryKey: APP_SETTINGS_KEY, queryFn: fetchAppSettings, staleTime: Number.POSITIVE_INFINITY });
    // S32 (Q26): density, columns and recently viewed follow the person; loaded with the settings.
    await context.queryClient.prefetchQuery({ queryKey: [...PREFERENCES_KEY, session.id], queryFn: () => loadPreferencesFor(session.id), staleTime: Number.POSITIVE_INFINITY });

    // S10: a hiring manager's world is the three HM screens (E16), so any S9 link
    // they open — an email, the bell, a bookmark — becomes the matching HM screen.
    if (session.isHiringManagerSurface) {
      const landing = await hmLandingFor(location.pathname, location.search, (id) =>
        needsOwnerAction(context.queryClient, id),
      );
      if (landing) throw redirect(landing);
    }
  },
  component: AppShell,
});

/**
 * SYS-04 inside the shell: an unknown address under the signed-in app keeps the header and menus
 * (CLAUDE.md §5a rule 6), and a signed-out visitor meets the app gate above and is sent to sign in.
 */
const appNotFoundRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '$',
  component: NotFound,
});

/**
 * Whether a requisition waits on its owner — read through the same cache entry
 * REQ-03 and the HM drawer use, so the landing costs no second request. Null when
 * it cannot be read, which leaves REQ-03 to show its own 403 or 404.
 */
async function needsOwnerAction(queryClient: QueryClient, id: string): Promise<boolean | null> {
  try {
    const detail = await queryClient.fetchQuery({
      queryKey: [...REQUISITIONS_KEY, 'detail', id],
      queryFn: () => apiGet<RequisitionDetail>(`/requisitions/${id}`),
    });
    return isWaitingOnOwner(detail.actions);
  } catch {
    return null;
  }
}

const STAGES: readonly DemoStage[] = [
  'screening',
  'assessment',
  'interview',
  'offer',
  'rejected',
];

function isStage(value: unknown): value is DemoStage {
  return typeof value === 'string' && STAGES.includes(value as DemoStage);
}

function isPositionStatus(value: unknown): value is PositionStatus {
  return typeof value === 'string' && POSITION_STATUSES.includes(value as PositionStatus);
}

const demoRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/demo',
  component: DemoPage,
  /**
   * Typed search params are how "URL-encoded filter state" is met without
   * inventing a serialiser (TECH-STACK 3.4). Anything unrecognised is dropped
   * rather than trusted — a URL is user input like any other.
   *
   * The return type is annotated so every key is genuinely optional. Returning an
   * object literal with `undefined` values instead would make the router treat
   * them as required, and every `<Link to="/demo">` in the app would have to pass
   * an empty search object.
   */
  validateSearch: (search: Record<string, unknown>): DemoSearch => {
    const next: { q?: string; stage?: DemoStage; sort?: string } = {};
    if (typeof search.q === 'string' && search.q !== '') next.q = search.q;
    if (isStage(search.stage)) next.stage = search.stage;
    if (typeof search.sort === 'string' && search.sort !== '') next.sort = search.sort;
    return next;
  },
});

// --- Organisation: ORG-01 to ORG-04 ------------------------------------------

const organisationRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/organisation',
  component: OrgTreePage,
});

const positionLedgerRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/organisation/positions',
  component: PositionLedgerPage,
  /**
   * The ledger's filters live in the URL, so the list is shareable by copying the
   * address bar (06-qol §3 and §6). Anything unrecognised is dropped rather than
   * trusted — a URL is user input like any other.
   *
   * The org-unit scope is deliberately NOT here. It is application-wide state set
   * from the tree, and duplicating it into this screen's query string would let a
   * shared link disagree with the scope indicator the recipient is looking at.
   */
  validateSearch: (search: Record<string, unknown>): PositionLedgerSearch => {
    const next: { q?: string; status?: string; archived?: PositionLedgerSearch['archived'] } = {};
    if (typeof search.q === 'string' && search.q !== '') next.q = search.q;
    const statuses =
      typeof search.status === 'string' ? search.status.split(',').filter(isPositionStatus) : [];
    if (statuses.length > 0) next.status = statuses.join(',');
    if (search.archived === '1' || search.archived === 'only') next.archived = search.archived;
    return next;
  },
});

const positionDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/organisation/positions/$positionId',
  component: PositionDetailPage,
});

const headcountPlanRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/organisation/headcount',
  component: HeadcountPlanPage,
});

// --- Requisitions: REQ-01 to REQ-05 (S9) ------------------------------------------

/** Every board filter lives in the URL (06-qol §6); anything that is not a string is dropped. */
function stringsOnly<T extends string>(search: Record<string, unknown>, keys: readonly T[]): Partial<Record<T, string>> {
  const next: Partial<Record<T, string>> = {};
  for (const key of keys) {
    const value = search[key];
    if (typeof value === 'string' && value !== '') next[key] = value;
  }
  return next;
}

const requisitionBoardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/requisitions',
  component: RequisitionBoardPage,
  validateSearch: (search: Record<string, unknown>): RequisitionBoardSearch =>
    stringsOnly(search, ['view', 'q', 'status', 'priority', 'sla', 'venture', 'department']),
});

const requisitionNewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/requisitions/new',
  component: RequestHirePage,
});

const requisitionDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/requisitions/$requisitionId',
  component: RequisitionDetailPage,
  /** `?decide=` is REQ-05 by deep link: N-01 and N-02 open the decision itself. */
  validateSearch: (search: Record<string, unknown>): RequisitionDetailSearch =>
    stringsOnly(search, ['decide']),
});

/** REQ-01 again, for a draft — and where N-06 "sent back" lands. */
const requisitionEditRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/requisitions/$requisitionId/edit',
  component: RequestHirePage,
});

/** REQ-04. N-69 "delegation started" lands here too. */
const approvalsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/approvals',
  component: ApprovalQueuePage,
});

// --- The hiring manager surface: HM-01 to HM-03 (S10) ------------------------------

/** HM-02. `?step=` and `?request=` are where a notification lands (non-negotiable #3). */
const myActionsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/my/actions',
  component: MyActionsPage,
  validateSearch: (search: Record<string, unknown>): MyActionsSearch => stringsOnly(search, ['step', 'request']),
});

/** HM-01. `?open=` opens one opening's drawer, so it is a link as well as a tap. */
const myOpeningsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/my/openings',
  component: MyOpeningsPage,
  validateSearch: (search: Record<string, unknown>): MyOpeningsSearch => stringsOnly(search, ['open']),
});

/**
 * R6 SHL-01: the manager's yes or no on a job's shortlist (§L31). On the HM surface, but open to
 * anyone named the job's Requested by or Line manager; the server decides (§L23). `?send=` is
 * N-89's "only the new names".
 */
const managerShortlistRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/my/shortlists/$jobId',
  validateSearch: (search: Record<string, unknown>): ManagerShortlistSearch => stringsOnly(search, ['send']),
  component: function ManagerShortlistRouteView() {
    const { jobId } = managerShortlistRoute.useParams();
    const search = managerShortlistRoute.useSearch();
    return <ManagerShortlistPage jobId={jobId} search={search} />;
  },
});

/** HM-03, and the same form for a draft or a request sent back. */
const requestHireRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/request-hire',
  component: RequestHirePage,
});

const requestHireEditRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/request-hire/$requisitionId',
  component: RequestHirePage,
});

// --- Jobs: JOB-01 to JOB-09 (S11, S12) ------------------------------------------------

const jobListRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/jobs',
  component: JobListPage,
  validateSearch: (search: Record<string, unknown>): JobListSearch => readJobListSearch(search),
});

/** F5.24 (R1): a job HR creates directly, with no requisition. Static, so it wins over `$jobId`. */
const newJobRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/jobs/new',
  component: NewJobPage,
});

/** JOB-02 before the job exists — where N-08 "approved, no job created" lands. */
const jobStartRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/jobs/new/$requisitionId',
  component: JobStartPage,
});

/**
 * A job's own address is JOB-12 (R2), opening on its current stage (DECISIONS-LOG
 * 2026-10-12 Q2) — where N-09 to N-13 land (§L22). A draft's current stage is ① Job, so it
 * opens on the builder (JOB-13, R3) at the first section still to finish (F5.13).
 */
const jobRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/jobs/$jobId',
  validateSearch: validatePipelineSearch,
  component: function JobLanding() {
    const { jobId } = jobRoute.useParams();
    const search: PipelineSearch = jobRoute.useSearch();
    const navigate = useNavigate();
    return <JobHubPage jobId={jobId} view={{ kind: 'landing' }} search={search} onSearch={(next) => void navigate({ to: '/jobs/$jobId', params: { jobId }, search: next, replace: true })} />;
  },
});

/** F6.05 — any staff member's referral links. */
const referralsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/referrals',
  component: ReferralsPage,
});

// --- S14: candidates and applications -------------------------------------------

/** CAND-01. Every filter, the sort, the page and the open pop-up live in the URL (06-qol §6). */
const candidatesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/candidates',
  component: CandidatesPage,
  validateSearch: (search: Record<string, unknown>): CandidateBrowseSearch => readBrowseSearch(search),
});

/** CAND-07. */
const addCandidateRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/candidates/new',
  component: AddCandidatePage,
});

/** CAND-06. N-17 lands on `/candidates/duplicates/$flagId` (§L22). */
const duplicatesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/candidates/duplicates',
  component: function DuplicateQueue() {
    return <DuplicatesPage />;
  },
});

const duplicateFlagRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/candidates/duplicates/$flagId',
  component: function DuplicateFlag() {
    const { flagId } = duplicateFlagRoute.useParams();
    return <DuplicatesPage focusFlagId={flagId} />;
  },
});

/** F7.07 — from a flag, or from "Merge with…" on a profile. */
const mergeRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/candidates/merge',
  validateSearch: (search: Record<string, unknown>): MergeSearch => stringsOnly(search, ['a', 'b', 'flag']),
  component: function MergeCandidates() {
    return <MergePage search={mergeRoute.useSearch()} />;
  },
});

/** CAND-02 — the candidate pop-up as a page; the open tab lives in the address. */
const candidateRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/candidates/$candidateId',
  validateSearch: (search: Record<string, unknown>): CandidateProfileSearch => readCandidateProfileSearch(search),
  component: function CandidateProfile() {
    const { candidateId } = candidateRoute.useParams();
    const { tab } = candidateRoute.useSearch();
    const navigate = useNavigate();
    return (
      <CandidateProfilePage
        key={candidateId}
        candidateId={candidateId}
        tab={tab ?? 'cv'}
        onTabChange={(next) => void navigate({ to: '/candidates/$candidateId', params: { candidateId }, search: next === 'cv' ? {} : { tab: next }, replace: true })}
      />
    );
  },
});

/** CAND-03 — where N-34, N-59 and N-60 will land (§L22). */
const applicationRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/applications/$applicationId',
  component: function ApplicationDetail() {
    const { applicationId } = applicationRoute.useParams();
    return <ApplicationPage key={applicationId} applicationId={applicationId} />;
  },
});

// --- S25: talent pools and search ------------------------------------------------

/**
 * POOL-04, folded into All candidates (Redesign Group 1). Old links and bookmarks land on the list
 * with their filters. Static, so it wins over `$candidateId`.
 */
const candidateSearchRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/candidates/search',
  validateSearch: (search: Record<string, unknown>): Record<string, unknown> => search,
  beforeLoad: ({ search }) => {
    throw redirect({ to: '/candidates', search: legacySearchToBrowse(search), replace: true });
  },
});

/** POOL-01. */
const poolsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/pools',
  component: PoolsPage,
});

/** ADR 0046 — the CV Bank. Search, page, the open CV and the duplicates live in the URL (06-qol §6). */
const cvBankRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/cv-bank',
  component: CvBankPage,
  validateSearch: (search: Record<string, unknown>): CvBankSearch => {
    const page = Number(search.page);
    return {
      ...stringsOnly(search, ['q', 'entry']),
      ...(Number.isInteger(page) && page > 1 ? { page } : {}),
      ...(search.dupes === true || search.dupes === 'true' ? { dupes: true } : {}),
    };
  },
});

/** POOL-02 — where N-64 lands (§L22). */
const poolRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/pools/$poolId',
  validateSearch: (search: Record<string, unknown>): PoolPageSearch => (search.filter === 'expiring' ? { filter: 'expiring' } : {}),
  component: function Pool() {
    const { poolId } = poolRoute.useParams();
    return <PoolPage key={poolId} poolId={poolId} />;
  },
});

// --- S26: tasks ------------------------------------------------------------------

/** COMM-04 — "the recruiter's morning screen". */
const tasksRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/tasks',
  component: function Tasks() {
    return <MyTasksPage />;
  },
});

/** Where N-62 and N-63 land: the list, with that task in view. */
const taskRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/tasks/$taskId',
  component: function Task() {
    const { taskId } = taskRoute.useParams();
    return <MyTasksPage focusTaskId={taskId} />;
  },
});

/** F20.04's "see all results" (§L10). */
const searchRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/search',
  validateSearch: (search: Record<string, unknown>): SearchResultsUrl => readSearchResults(search),
  component: SearchResultsPage,
});

// --- S17: assessments ------------------------------------------------------------

/**
 * ASM-02's list. Its own module rather than a page under Admin: §L23 gives recruiters
 * the bank and builder, and Admin is the HR Lead's (the same reasoning as Organisation).
 */
const testsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/assessments',
  component: TestsPage,
});

/** ASM-01. Filters and the open editor live in the address (06-qol §6). */
const questionBankRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/assessments/questions',
  component: QuestionBankPage,
  validateSearch: (search: Record<string, unknown>): QuestionBankSearch => {
    const page = Number(search.page);
    return {
      ...stringsOnly(search, ['q', 'type', 'difficulty', 'tag', 'family', 'edit']),
      ...(Number.isInteger(page) && page > 1 ? { page } : {}),
    };
  },
});

/** ASM-02 — one test, or `new`. */
const testBuilderRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/assessments/tests/$testId',
  component: TestBuilderPage,
});

// --- S18: exam sessions and marking ---------------------------------------------------

/** ASM-04's list. An invigilator's only screen under Assessments (§L23). */
const examSessionsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/assessments/sessions',
  component: ExamSessionsPage,
});

/** ASM-04 / ASM-05 — where N-26 lands. Keyed, so moving between sessions starts fresh. */
const examSessionRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/assessments/sessions/$sessionId',
  component: function ExamSession() {
    const { sessionId } = examSessionRoute.useParams();
    return <ExamSessionPage key={sessionId} sessionId={sessionId} />;
  },
});

/** ASM-05's printed list. Inside the app (it needs the session); the shell hides in print. */
const examSessionPrintRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/assessments/sessions/$sessionId/print',
  component: function ExamSessionPrint() {
    const { sessionId } = examSessionPrintRoute.useParams();
    return <PrintRosterPage sessionId={sessionId} />;
  },
});

/** ASM-06's queue. */
const markingQueueRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/assessments/marking',
  component: MarkingQueuePage,
});

/** ASM-06 — one test's written answers; where N-24 and N-25 land. */
const markingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/assessments/marking/$attemptId',
  component: function Marking() {
    const { attemptId } = markingRoute.useParams();
    return <MarkingPage key={attemptId} attemptId={attemptId} />;
  },
});

// --- S19: interview scheduling -------------------------------------------------------------

/** SCH-02 — the week. The Interviews module's first page. */
const interviewCalendarRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/interviews',
  component: InterviewCalendarPage,
});

/** SCH-02 — waiting for a time. */
const toScheduleRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/interviews/to-schedule',
  component: ToSchedulePage,
});

/** SCH-03. */
const myInterviewsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/interviews/mine',
  component: MyInterviewsPage,
});

/** F2.12 for everyone — a panelist can't open USER-02. */
const myAvailabilityRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/interviews/availability',
  component: MyAvailabilityPage,
});

/** SCH-04 — where N-29–N-35 and N-77 land. Keyed, so moving between interviews starts fresh. */
const interviewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/interviews/$interviewId',
  component: function Interview() {
    const { interviewId } = interviewRoute.useParams();
    return <InterviewPage key={interviewId} interviewId={interviewId} />;
  },
});

/** SCH-01 — where N-36 lands. */
const schedulerRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/interviews/$interviewId/schedule',
  component: function Scheduler() {
    const { interviewId } = schedulerRoute.useParams();
    return <SchedulerPage key={interviewId} interviewId={interviewId} />;
  },
});

// --- S20: the interview cockpit and scorecards -------------------------------------------

/** INT-01 — where N-31, N-37, N-38 and N-39 land. Keyed, so moving between interviews starts fresh. */
const cockpitRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/interviews/$interviewId/scorecard',
  component: function Cockpit() {
    const { interviewId } = cockpitRoute.useParams();
    return <CockpitPage key={interviewId} interviewId={interviewId} />;
  },
});

/** INT-03 — where N-40 lands until RPT-04 exists (S29). */
const interviewScorecardsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/interviews/$interviewId/scorecards',
  component: function InterviewScorecards() {
    const { interviewId } = interviewScorecardsRoute.useParams();
    return <InterviewScorecardsPage key={interviewId} interviewId={interviewId} />;
  },
});

// --- S21: round debrief and stage decisions ----------------------------------------

/** DEC-01 — where N-41, N-42, N-43 and N-44 land. Keyed, so moving between rounds starts fresh. */
const debriefRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/interviews/$interviewId/debrief',
  component: function Debrief() {
    const { interviewId } = debriefRoute.useParams();
    return <DebriefPage key={interviewId} interviewId={interviewId} />;
  },
});

// --- S15: sorting mode ------------------------------------------------------------

/**
 * SCR-01 — where N-15 and N-16 land (§L22), and "Start sorting" on JOB-10. A static
 * segment, so it outranks `/jobs/$jobId/$tab`. Keyed by job, so switching jobs
 * starts a fresh session rather than reusing one screen's queue.
 */
const sortingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/jobs/$jobId/sort',
  component: function Sorting() {
    const { jobId } = sortingRoute.useParams();
    return <SortingPage key={jobId} jobId={jobId} />;
  },
});

/**
 * SCR-02 / SCR-03 — every stage at once, "All candidates" in JOB-12's ⋯ menu, where N-73
 * lands with its report open. A static segment, so it outranks `/jobs/$jobId/$tab`; its
 * filters, layout and view live in the address (06-qol §6).
 */
const pipelineRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/jobs/$jobId/pipeline',
  validateSearch: validatePipelineSearch,
  component: function Pipeline() {
    const { jobId } = pipelineRoute.useParams();
    const search: PipelineSearch = pipelineRoute.useSearch();
    const navigate = useNavigate();
    return <JobHubPage jobId={jobId} view={{ kind: 'panel', panel: 'pipeline' }} search={search} onSearch={(next) => void navigate({ to: '/jobs/$jobId/pipeline', params: { jobId }, search: next, replace: true })} />;
  },
});

/** SCR-04 — where N-18 lands. */
const autoRejectedRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/jobs/$jobId/auto-rejected',
  component: function AutoRejected() {
    const { jobId } = autoRejectedRoute.useParams();
    return <AutoRejectedPage jobId={jobId} />;
  },
});

/** OFR-01 and OFR-03 — one offer, built and tracked. N-48–N-54, N-58, N-80 and N-81 land here. */
const offerRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/offers/$offerId',
  component: function Offer() {
    const { offerId } = offerRoute.useParams();
    return <OfferPage offerId={offerId} />;
  },
});

/** OFR-04 — the joining: documents, then the hire. N-56 and N-57 land here. */
const hireRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/offers/$offerId/joining',
  component: function Hire() {
    const { offerId } = hireRoute.useParams();
    return <HirePage offerId={offerId} />;
  },
});

/** HIRE-01 (ADR 0083) — a hire's 30 and 90-day plans. N-103 lands here; HR comes from the job's Selected step. */
const hirePlansRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/hires/$applicationId/plans',
  component: function HirePlans() {
    const { applicationId } = hirePlansRoute.useParams();
    return <HirePlansPage applicationId={applicationId} />;
  },
});

/** OFR-05 — closing a job, or closing out a filled one. A static segment, so it wins over `$tab`. */
const closureRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/jobs/$jobId/close',
  component: function Closure() {
    const { jobId } = closureRoute.useParams();
    return <ClosurePage jobId={jobId} />;
  },
});

/**
 * JOB-12's stages (QoL §15: `/jobs/{id}/{stage}`), and JOB-10's old tabs, one address each —
 * N-10 still opens Postings (§L22 POST-01). A stage's list filters live in the address too.
 */
const jobTabRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/jobs/$jobId/$tab',
  validateSearch: validatePipelineSearch,
  component: function JobStagePage() {
    const { jobId, tab } = jobTabRoute.useParams();
    const search: PipelineSearch = jobTabRoute.useSearch();
    const navigate = useNavigate();
    return <JobHubPage jobId={jobId} view={jobPageViewOf(tab)} search={search} onSearch={(next) => void navigate({ to: '/jobs/$jobId/$tab', params: { jobId, tab }, search: next, replace: true })} />;
  },
});

/**
 * JOB-13's sections (R3), each its own address under stage ① Job. The wizard's old step
 * addresses (`/setup/criteria`, `/setup/interview`…) land on the matching section, so a link in
 * an old notification still works (non-negotiable #3).
 */
const jobSetupRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/jobs/$jobId/setup/$section',
  validateSearch: validatePipelineSearch,
  component: function JobSetupPage() {
    const { jobId, section } = jobSetupRoute.useParams();
    const search: PipelineSearch = jobSetupRoute.useSearch();
    const navigate = useNavigate();
    const view = { kind: 'stage', stage: 'job', section: isJobBuilderSection(section) ? section : sectionForWizardStep(section) } as const;
    return <JobHubPage jobId={jobId} view={view} search={search} onSearch={(next) => void navigate({ to: '/jobs/$jobId/setup/$section', params: { jobId, section }, search: next, replace: true })} />;
  },
});

/**
 * One placeholder per module, so the permission-driven module bar has somewhere
 * to navigate to and each slice replaces its own without touching the router.
 *
 * `admin` is no longer among them — S4 builds it for real. Nor is `organisation`,
 * which S5 builds above.
 */
const moduleRoutes = NAV_ITEMS.filter(
  (item) => !['admin', 'organisation', 'requisitions', 'jobs', 'candidates', 'assessments', 'interviews', 'tasks', 'dashboard', 'reports'].includes(item.id),
).map((item) =>
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path: item.to,
    component: () => <PlaceholderPage moduleId={item.id} />,
  }),
);

// --- S27: the Dashboard ------------------------------------------------------------

/** RPT-02 for a recruiter (Q13); everyone else is sent to their own first screen. */
const homeRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  component: HomePage,
});

/** "My workspace", by name — the HR Lead reaches theirs from the command center (Q13). */
const workspaceRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/workspace',
  component: WorkspacePage,
});

// --- S28: reports ------------------------------------------------------------------

/** The Reports module itself: the command center for an HR Lead, the definitions for anyone else. */
const reportsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/reports',
  component: ReportsIndexPage,
});

/** RPT-01, with F17.03's filters in the address. */
const commandCenterRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/reports/command-center',
  validateSearch: (search: Record<string, unknown>): CommandCenterUrl => readCommandCenterUrl(search),
  component: CommandCenterPage,
});

/** F17.02 — the records behind one figure. */
const drillRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/reports/drill',
  validateSearch: (search: Record<string, unknown>): DrillPageUrl => ({ ...readDrillUrl(search), ...readAnalyticsDrillUrl(search) }),
  component: DrillPage,
});

/** Q19 — the metric dictionary, read-only until ADMIN-14. */
// --- S29: RPT-03 to RPT-06 ---------------------------------------------------------

const proficiencyRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/reports/proficiency',
  validateSearch: (search: Record<string, unknown>): AnalyticsUrl => readAnalyticsUrl(search),
  component: ProficiencyPage,
});

const scoreboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/reports/scoreboard',
  validateSearch: (search: Record<string, unknown>): AnalyticsUrl => readAnalyticsUrl(search),
  component: ScoreboardPage,
});

const dropOffRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/reports/drop-off',
  validateSearch: (search: Record<string, unknown>): DropOffUrl => readDropOffUrl(search),
  component: DropOffPage,
});

const builderRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/reports/builder',
  validateSearch: (search: Record<string, unknown>): BuilderUrl => readBuilderUrl(search),
  component: ReportBuilderPage,
});

const metricsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/reports/metrics',
  component: MetricDictionaryPage,
});

// --- Admin: USER-01 to USER-06 -----------------------------------------------

const adminRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/admin',
  component: AdminLayout,
});

/** `/admin` opens Overview for Admin and the first page they have for anyone else (ADR 0073, Admin redesign 2026-11-02). */
const adminLandingRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/',
  component: AdminLanding,
});

/** ADMIN-21, Admin's home. */
const adminOverviewRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/overview',
  component: AdminOverviewPage,
});

/** USER-01. Was `/admin` until Overview took the landing (2026-11-02); `/admin` still opens Admin. */
const usersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/users',
  component: UserListPage,
});

const userDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/users/$userId',
  component: UserDetailPage,
});

const inviteRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/invite',
  component: InviteUserPage,
});

const rolesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/roles',
  // The open role is in the address (`?role=<id>`), so a link can open one role directly.
  validateSearch: (search: Record<string, unknown>): RolesSearch => readRolesSearch(search),
  component: RoleBuilderPage,
});

const delegationRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/delegation',
  component: DelegationPage,
});

/** USER-06: the kind, Who, When, the search and the open entry live in the address (Admin redesign 2026-11-02). */
const auditRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/audit',
  validateSearch: (search: Record<string, unknown>): AccessLogSearch => readAccessLogSearch(search),
  component: AccessAuditPage,
});

/** S7's event inspector. HR Lead only; the guard is the screen's own `useCanView`. */
const eventLogRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/events',
  component: EventLogPage,
});

/** ADMIN-10 — S6. Working hours and the holiday calendar. */
const calendarsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/calendars',
  component: CalendarsPage,
});

/** F10.14's rooms (S19 Q5) — only the list; the rest of admin configuration is S31. */
const roomsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/rooms',
  component: RoomsPage,
});

/** S30 — retention, anonymisation and the monthly archives; N-85 lands with `archive=`. */
const retentionRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/retention',
  validateSearch: (search: Record<string, unknown>): RetentionUrl => (typeof search.archive === 'string' && /^[0-9a-f-]{36}$/i.test(search.archive) ? { archive: search.archive } : {}),
  component: RetentionPage,
});

/**
 * ADMIN Email delivery (Admin redesign batch 2): the pill, search, Sent to, When, the page and the open email live in
 * the address. Admin settings only; the guard is the screen's own `useCanView`.
 *
 * Note what is NOT a route here: Bull Board at `/admin/queues`. It is a server-rendered page mounted on Fastify, not a
 * screen in this application, so Background work links to it as "Technical view ↗", a real anchor in a new tab.
 */
const emailDeliveryRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/email',
  validateSearch: (search: Record<string, unknown>): EmailDeliverySearch => readEmailDeliverySearch(search),
  component: EmailDeliveryPage,
});

/** ADMIN Background work (Admin redesign batch 2, owner Q3): our own page over the queues; tab, queue, list and job in the address. */
const backgroundWorkRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/background-work',
  validateSearch: (search: Record<string, unknown>): BackgroundWorkSearch => readBackgroundWorkSearch(search),
  component: BackgroundWorkPage,
});

/** ADMIN-06 — S9. Approval chains (Admin redesign 2026-11-02, option A): the tab and the chain open are in the address. */
const approvalChainsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/approval-chains',
  validateSearch: (search: Record<string, unknown>): ApprovalChainsSearch => readApprovalChainsSearch(search),
  component: ApprovalChainsPage,
});

/** Admin · Time limits (Admin redesign 2026-11-02): the requisition time limits, their own page, gated on Time limits only. */
const timeLimitsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/time-limits',
  component: TimeLimitsPage,
});

/** ADMIN-16 — S23. Offer letters (DECISIONS-LOG S23 Q1). */
const offerLettersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/offer-letters',
  component: OfferLettersPage,
});

/** ADMIN-07 — S15. Rejection and withdrawal reasons (F19.07). */
const reasonsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/reasons',
  component: ReasonsPage,
});

/** ADMIN-12 — S13. Career site branding. */
const careerSiteRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/career-site',
  component: CareerSitePage,
});

/** ADMIN-15 — S31. The organisation's settings. */
/** ADMIN-17 — R1 (§L28). One switch per part of the app. */
const masterSettingsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/master-settings',
  component: MasterSettingsPage,
});

const systemSettingsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/settings',
  component: SystemSettingsPage,
});

/** ADMIN-08 — S31. Where candidates come from. */
const sourcesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/sources',
  component: SourcesPage,
});

/** ADMIN-19 — F19.14. The tools box 1 of the ideal candidate profile offers; HR deletes or merges typos. */
const toolNamesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/tool-names',
  component: ToolNamesPage,
});

/** ADR 0074. The staff list "Who is being replaced?" reads. */
const staffListRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/staff',
  component: StaffListPage,
});

/** D13 — S31. The FX table "Show converted" reads. */
const fxRatesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/fx-rates',
  component: FxRatesPage,
});

/** ADMIN-13 — S31. The organisation's own fields. */
const customFieldsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/custom-fields',
  component: CustomFieldsPage,
});

/** §L2's name table — its own screen since S31 (Q23). */
const nameMatchingRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/name-matching',
  component: NameMatchingPage,
});

/** ADMIN-01 — S11. Pipelines, one per role family. */
const pipelinesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/pipelines',
  component: PipelinesPage,
});

const pipelineEditorRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/pipelines/$pipelineId',
  component: PipelineEditorPage,
});

/** ADMIN-05 — S11. The role template library, and the department drafts to decide. */
const roleTemplatesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/role-templates',
  component: RoleTemplatesPage,
});

const roleTemplateEditorRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/role-templates/$templateId',
  component: RoleTemplateEditorPage,
});

/** ADMIN-02 — S12. Scorecard rubrics, versioned on edit. */
const rubricsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/rubrics',
  component: RubricsPage,
});

const rubricEditorRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/rubrics/$rubricId',
  component: RubricEditorPage,
});

/** ADMIN-04 — S12. Interview kits: questions mapped to rubric lines. */
const interviewKitsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/interview-kits',
  component: InterviewKitsPage,
});

const interviewKitEditorRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/interview-kits/$kitId',
  component: InterviewKitEditorPage,
});

/** ADMIN-03 — S12. Interview plan templates. */
const planTemplatesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/interview-plans',
  component: PlanTemplatesPage,
});

const planTemplateEditorRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/interview-plans/$planId',
  component: PlanTemplateEditorPage,
});

// --- Notifications: NOTIF-01 and NOTIF-02 ------------------------------------

function isNotificationCategory(value: unknown): value is NotificationCategory {
  return (
    typeof value === 'string' &&
    (NOTIFICATION_CATEGORIES as readonly string[]).includes(value)
  );
}

const notificationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/notifications',
  component: NotificationInboxPage,
  /**
   * The filters live in the URL so the list is shareable, and `?focus=` is how a
   * purely informational notification points at itself. Anything unrecognised is
   * dropped rather than trusted — a URL is user input like any other.
   */
  validateSearch: (search: Record<string, unknown>): NotificationInboxSearch => {
    const next: { filter?: 'unread'; category?: NotificationCategory; focus?: string } = {};
    if (search.filter === 'unread') next.filter = 'unread';
    if (isNotificationCategory(search.category)) next.category = search.category;
    if (typeof search.focus === 'string' && search.focus !== '') next.focus = search.focus;
    return next;
  },
});

const notificationPreferencesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/notifications/preferences',
  component: NotificationPreferencesPage,
});

/** ADMIN-09 (S26). */
const messageTemplatesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/message-templates',
  component: MessageTemplatesPage,
});

/** F15.04's unassigned queue (S26). */
const inboxRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/inbox',
  component: InboxPage,
});

/** ADMIN-11 (S25). */
const poolsAndTagsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/pools-and-tags',
  component: PoolsAndTagsPage,
});

const routeTree = rootRoute.addChildren([
  testPlayerRoute,
  interviewBookingRoute,
  scorecardLinkRoute,
  candidateOfferRoute,
  authLayoutRoute.addChildren([
    signedOutOnlyRoute.addChildren([
      loginRoute,
      forgotPasswordRoute,
      resetPasswordRoute,
    ]),
    acceptInviteRoute,
    sessionExpiredRoute,
    accountLockedRoute,
    otpChallengeRoute,
    twoFactorSetupRoute,
    welcomeRoute,
  ]),
  appLayoutRoute.addChildren([
    demoRoute,
    organisationRoute,
    positionLedgerRoute,
    positionDetailRoute,
    headcountPlanRoute,
    requisitionBoardRoute,
    requisitionNewRoute,
    requisitionDetailRoute,
    requisitionEditRoute,
    approvalsRoute,
    myActionsRoute,
    myOpeningsRoute,
    managerShortlistRoute,
    requestHireRoute,
    requestHireEditRoute,
    jobListRoute,
    newJobRoute,
    jobStartRoute,
    jobRoute,
    sortingRoute,
    autoRejectedRoute,
    pipelineRoute,
    jobTabRoute,
    jobSetupRoute,
    offerRoute,
    hireRoute,
    hirePlansRoute,
    closureRoute,
    notificationsRoute,
    notificationPreferencesRoute,
    adminRoute.addChildren([
      adminLandingRoute,
      adminOverviewRoute,
      usersRoute,
      userDetailRoute,
      inviteRoute,
      rolesRoute,
      delegationRoute,
      auditRoute,
      calendarsRoute,
      roomsRoute,
      retentionRoute,
      eventLogRoute,
      emailDeliveryRoute,
      backgroundWorkRoute,
      approvalChainsRoute,
      timeLimitsRoute,
      offerLettersRoute,
      reasonsRoute,
      pipelinesRoute,
      pipelineEditorRoute,
      roleTemplatesRoute,
      roleTemplateEditorRoute,
      rubricsRoute,
      rubricEditorRoute,
      interviewKitsRoute,
      interviewKitEditorRoute,
      planTemplatesRoute,
      planTemplateEditorRoute,
      careerSiteRoute,
      masterSettingsRoute,
      systemSettingsRoute,
      sourcesRoute,
      toolNamesRoute,
      staffListRoute,
      fxRatesRoute,
      customFieldsRoute,
      nameMatchingRoute,
      poolsAndTagsRoute,
      messageTemplatesRoute,
      inboxRoute,
    ]),
    referralsRoute,
    candidatesRoute,
    candidateSearchRoute,
    poolsRoute,
    poolRoute,
    cvBankRoute,
    searchRoute,
    tasksRoute,
    taskRoute,
    addCandidateRoute,
    duplicatesRoute,
    duplicateFlagRoute,
    mergeRoute,
    candidateRoute,
    applicationRoute,
    testsRoute,
    questionBankRoute,
    testBuilderRoute,
    examSessionsRoute,
    examSessionRoute,
    examSessionPrintRoute,
    markingQueueRoute,
    markingRoute,
    interviewCalendarRoute,
    toScheduleRoute,
    myInterviewsRoute,
    myAvailabilityRoute,
    interviewRoute,
    schedulerRoute,
    cockpitRoute,
    interviewScorecardsRoute,
    debriefRoute,
    homeRoute,
    workspaceRoute,
    reportsRoute,
    commandCenterRoute,
    drillRoute,
    proficiencyRoute,
    scoreboardRoute,
    dropOffRoute,
    builderRoute,
    metricsRoute,
    ...moduleRoutes,
    appNotFoundRoute,
  ]),
]);

/**
 * A router instance.
 *
 * A factory as well as a singleton, because a router carries navigation state and
 * a resolved context — and a test that reuses one instance inherits wherever the
 * previous test left it. The application uses the singleton below; each test makes
 * its own.
 */
export function createAppRouter() {
  return createRouter({
    routeTree,
    // Filled in by `main.tsx`, which owns the QueryClient. Declared here so the
    // route tree can be typed against it.
    context: { queryClient: undefined as unknown as QueryClient },
    // Shown only while the very first session check is in flight; every later
    // navigation reads it from cache and resolves without a frame of loading.
    defaultPendingComponent: ShellSkeleton,
    // A page that couldn't load gets SYS-05 with Try again, not the router's unstyled default.
    defaultErrorComponent: RouteErrorState,
    defaultPreload: 'intent',
    // Prefetch on hover for links the user is likely to click (06-qol §5).
    defaultPreloadStaleTime: 0,
  });
}

export const router = createAppRouter();

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
  interface HistoryState {
    /** S16 "Sort selected": the applications SCR-02 hands to sorting mode, kept out of the address. */
    readonly sortOnly?: readonly string[];
  }
}
